import { watch, ref } from "vue";
import { getFromLocalStorage } from "./function-local-storage";

// The Android WebView has no working Web Speech API, so the app uses native plugins instead
// (loaded with a dynamic import so they are compiled out of the website build)
const isNative = !!import.meta.env.QUASAR_CAPACITOR_MODE

const DEFAULT_VOICE = { label: 'Google US English', lang: 'en-US' }

// --- SpeechRecognition ---
// Guard for browsers that don't support the API
const SpeechRecognitionAPI = isNative ? null : window.SpeechRecognition || window.webkitSpeechRecognition;

// Module-level singleton state — shared across ALL consumers of useSpeechRecognition()
const isListening = ref(false)
const spokenText = ref("")
const error = ref(null)

let recognition = null

if (SpeechRecognitionAPI) {
  recognition = new SpeechRecognitionAPI()
  recognition.interimResults = true

  recognition.onresult = (event) => {
    const result = event.results[event.results.length - 1]
    spokenText.value = result[0].transcript
  }

  recognition.onerror = (event) => {
    error.value = event.error
    isListening.value = false
  }

  recognition.onstart = () => {
    isListening.value = true
  }

  // onend fires both when recognition stops naturally (after silence) and when
  // stop() is called manually. Setting isListening = false here will trigger the
  // watcher below, which will call recognition.stop() again. The try/catch in the
  // watcher makes that harmless — it just swallows the "already stopped" DOMException.
  recognition.onend = () => {
    isListening.value = false
  }

  // Single module-level watcher — only set up once, not once per useSpeechRecognition() call.
  // Re-reads the language preference from localStorage each time recognition starts so that
  // changes made in TtsSettingsModal take effect immediately on the next session.
  watch(isListening, (listening) => {
    if (listening) {
      const opts = getFromLocalStorage('ttsOptions')
      recognition.lang = opts?.selectedVoice?.lang ?? 'en-US'
      try { recognition.start() } catch (e) { /* already running */ }
    } else {
      try { recognition.stop() } catch (e) { /* already stopped — harmless */ }
    }
  })
}

// NOTE: a Capacitor plugin object is a proxy that forwards ANY property to native code, including
// `.then` — so it must never be the resolved value of a promise (an async function's return value,
// a .then() callback's return value), or the promise calls "Plugin.then()" natively and rejects.
// These helpers resolve with the plugin's *module* instead; read the plugin off it after awaiting.

// Native recognition: listeners are registered once and write into the same shared refs
let nativeRecognitionModule = null

function loadNativeRecognition() {
  if (!nativeRecognitionModule) {
    nativeRecognitionModule = import('@capacitor-community/speech-recognition').then(async (module) => {
      const { SpeechRecognition } = module
      await SpeechRecognition.addListener('partialResults', ({ matches }) => {
        if (matches?.length) spokenText.value = matches[0]
      })
      // fires 'stopped' when Android ends the session by itself (after silence)
      await SpeechRecognition.addListener('listeningState', ({ status }) => {
        isListening.value = status === 'started'
      })
      return module
    })
  }
  return nativeRecognitionModule
}

async function toggleNativeListening() {
  error.value = null
  try {
    const { SpeechRecognition } = await loadNativeRecognition()

    if (isListening.value) {
      isListening.value = false
      await SpeechRecognition.stop()
      return
    }

    const { available } = await SpeechRecognition.available()
    if (!available) {
      error.value = 'not-available'
      return
    }

    let { speechRecognition: permission } = await SpeechRecognition.checkPermissions()
    if (permission !== 'granted') ({ speechRecognition: permission } = await SpeechRecognition.requestPermissions())
    if (permission !== 'granted') {
      error.value = 'not-allowed'
      return
    }

    spokenText.value = ''
    isListening.value = true
    const opts = getFromLocalStorage('ttsOptions')
    // with partialResults the call returns right away; text arrives through the listener
    await SpeechRecognition.start({
      language: opts?.selectedVoice?.lang ?? 'en-US',
      partialResults: true,
      popup: false,
    })
  } catch (e) {
    error.value = e?.message || String(e)
    isListening.value = false
  }
}

export function useSpeechRecognition() {
  const toggleListening = () => {
    if (isNative) return toggleNativeListening()

    if (!recognition) {
      console.warn('SpeechRecognition is not supported in this browser.')
      return
    }
    // Clear previous result when starting a new recording session
    if (!isListening.value) spokenText.value = ''
    isListening.value = !isListening.value
  }

  return {
    toggleListening,
    spokenText,
    error,
    isListening,
  }
}

// --- TextToSpeech ---

let nativeTtsModule = null
let nativeVoices = null

// resolves with the module, never the plugin proxy itself (see the NOTE above)
function loadNativeTts() {
  if (!nativeTtsModule) nativeTtsModule = import('@capacitor-community/text-to-speech')
  return nativeTtsModule
}

// the plugin selects a voice by its index in this list, so it is cached once
async function getNativeVoiceList() {
  if (nativeVoices) return nativeVoices

  const { TextToSpeech } = await loadNativeTts()
  const voices = (await TextToSpeech.getSupportedVoices()).voices || []
  // the engine starts asynchronously and may report no voices at first: don't cache that
  if (voices.length) nativeVoices = voices
  return voices
}

// Voices for the TTS preferences, as { label, lang } (same shape as the saved selectedVoice)
export async function getVoiceOptions() {
  if (isNative) {
    const voices = await getNativeVoiceList()
    return voices.map(voice => ({ label: voice.name, lang: voice.lang }))
  }
  return window.speechSynthesis.getVoices().map(voice => ({ label: voice.name, lang: voice.lang }))
}

async function speakNative(text, isSpeaking) {
  if (!text.trim().length) return true

  const ttsOptions = getFromLocalStorage('ttsOptions')
  const selectedVoice = ttsOptions ? ttsOptions.selectedVoice : DEFAULT_VOICE

  isSpeaking.value = true
  try {
    const { TextToSpeech } = await loadNativeTts()
    const voiceIndex = (await getNativeVoiceList()).findIndex(voice => voice.name === selectedVoice?.label)

    // resolves when the utterance has finished; a new speak() interrupts the current one (queueStrategy 0 = Flush)
    await TextToSpeech.speak({
      text,
      lang: selectedVoice?.lang || 'en-US',
      rate: Math.max(0.1, ttsOptions ? ttsOptions.rate : 1),
      pitch: Math.max(0.1, ttsOptions ? ttsOptions.pitch : 1),
      volume: Math.min(1, ttsOptions ? ttsOptions.volume : 1),
      queueStrategy: 0,
      // a voice saved on another device/browser may not exist here: fall back to the language's default voice
      ...(voiceIndex >= 0 && { voice: voiceIndex }),
    })
  } catch (e) {
    console.warn('native tts failed', e)
  } finally {
    isSpeaking.value = false
  }
  return true
}

export function useTextToSpeech() {
  const isSpeaking = ref(false)
  const speak = (text = 'no text here') => {
    if (isNative) return speakNative(text, isSpeaking)

    return new Promise((resolve, reject) => {
      let utterance = new SpeechSynthesisUtterance()
      let ttsOptions = getFromLocalStorage('ttsOptions')

      utterance.text = text
      utterance.volume = ttsOptions ? ttsOptions.volume : 1
      utterance.rate = ttsOptions ? ttsOptions.rate : 1
      utterance.pitch = ttsOptions ? ttsOptions.pitch : 1

      let speechSynthesis = window.speechSynthesis
      let voices = speechSynthesis.getVoices()

      let selectedVoice = ttsOptions ? ttsOptions.selectedVoice : DEFAULT_VOICE
      for (let index = 0; index < voices.length; index++) {
        if(voices[index].name === selectedVoice.label) {
            utterance.voice = voices[index];
          break;
        }
      }

      setTimeout(() => {
        speechSynthesis.speak(utterance)
      }, 100)

      utterance.addEventListener('start', event => {
        if(text.trim().length) isSpeaking.value = true
      })

      utterance.addEventListener('boundary', event => {})

      utterance.onboundary = function(event) {}

      utterance.addEventListener('end', event => {
        isSpeaking.value = false
        resolve(true)
      })
    })
  }

  // Android's TTS engine can't pause, so in the app pause/resume act as "stop"
  const pause = () => {
    if (isNative) return loadNativeTts().then(({ TextToSpeech }) => TextToSpeech.stop())
    window.speechSynthesis.pause()
  }

  const resume = () => {
    if (isNative) return loadNativeTts().then(({ TextToSpeech }) => TextToSpeech.stop())
    window.speechSynthesis.resume()
  }

  return {
    speak,
    isSpeaking,
    pause,
    resume,
  }
}
